import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, ChevronDown } from "lucide-react";
import { MARKET_PROFESSIONS, CATEGORY_COLORS } from "@/components/market/marketData";
import ProfessionCard from "@/components/market/ProfessionCard";
import ProfessionDetail from "@/components/market/ProfessionDetail";
import IncomeSimulator from "@/components/market/IncomeSimulator";
import CareerComparison from "@/components/market/CareerComparison";
import { db } from "@/api/db";
import { calculateResult } from "@/components/vocational/vocationalAlgorithm";
import { useAuth } from "@/lib/AuthContext";

const CATEGORIES = ["Todas", ...Object.keys(CATEGORY_COLORS)];
const VALID_VIEWS = ["list", "detail", "simulator", "comparison"];

function getViewFromParams(search) {
  const params = new URLSearchParams(search);
  const v = params.get("view");
  return VALID_VIEWS.includes(v) ? v : null;
}

export default function MarketMap() {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const professionParam = urlParams.get("profession");

  const initialProfession = professionParam
    ? MARKET_PROFESSIONS.find(p => p.name === decodeURIComponent(professionParam)) || null
    : null;

  const initialView = initialProfession ? "detail" : (getViewFromParams(location.search) || "list");

  const [view, setView] = useState(initialView);
  const [selectedProfession, setSelectedProfession] = useState(initialProfession);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Todas");
  const [recommendedProfessions, setRecommendedProfessions] = useState(new Set());

  const { user } = useAuth();

  // Sync view when URL search params change (sidebar navigation)
  useEffect(() => {
    const v = getViewFromParams(location.search);
    if (v) {
      setView(v);
    } else if (!urlParams.get("profession")) {
      setView("list");
    }
  }, [location.search]);

  useEffect(() => {
    if (!user?.email) return;
    (async () => {
      try {
        const profiles = await db.entities.UserProfile.filter({ user_email: user.email }, "-created_date", 1);
        const p = profiles[0];
        if (p?.vocational_completed && p?.vocational_answers) {
          const result = calculateResult(p.vocational_answers);
          const top5 = result.topProfessions.slice(0, 5).map(t => t.name);
          setRecommendedProfessions(new Set(top5));
        }
      } catch {
        // silently ignore — recommendations just won't appear
      }
    })();
  }, [user?.email]);

  const filtered = useMemo(() => {
    return MARKET_PROFESSIONS.filter(p => {
      const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.tagline.toLowerCase().includes(search.toLowerCase());
      const matchCat = category === "Todas" || p.category === category;
      return matchSearch && matchCat;
    });
  }, [search, category]);

  const grouped = useMemo(() => {
    if (category !== "Todas") return null;
    const groups = {};
    for (const p of filtered) {
      if (!groups[p.category]) groups[p.category] = [];
      groups[p.category].push(p);
    }
    return groups;
  }, [filtered, category]);

  function openDetail(profession) {
    setSelectedProfession(profession);
    setView("detail");
  }

  function openSimulator(profession) {
    setSelectedProfession(profession);
    setView("simulator");
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Mapa do Mercado
        </h1>
        <p className="text-sm mt-1" style={{ color: "#646C6F" }}>
          Como é viver de cada profissão no mundo real
        </p>
      </div>

      <AnimatePresence mode="wait">
        {(view === "list") && (
          <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#99A6AB" }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar profissão..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border focus:outline-none"
                style={{ borderColor: "#E5E9EA", background: "#fff", color: "#1A1D1E" }}
              />
            </div>

            {/* Category filter */}
            <div className="relative">
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full text-sm font-semibold px-4 py-2.5 rounded-xl appearance-none focus:outline-none cursor-pointer"
                style={{ background: "#F2F4F4", color: "#003326", border: "1px solid #E5E9EA" }}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: "#99A6AB" }} />
            </div>

            {/* Cards */}
            <div className="space-y-2">
              <AnimatePresence mode="popLayout">
                {filtered.length === 0 ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-12 text-sm"
                    style={{ color: "#99A6AB" }}
                  >
                    Nenhuma profissão encontrada
                  </motion.div>
                ) : grouped ? (
                  Object.entries(grouped).map(([cat, profs]) => {
                    const colors = CATEGORY_COLORS[cat] || { dot: "#99A6AB", text: "#646C6F" };
                    return (
                      <motion.div
                        key={cat}
                        layout
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25 }}
                      >
                        <div className="flex items-center gap-2 mt-4 mb-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: colors.dot }} />
                          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.text }}>
                            {cat}
                          </span>
                          <span className="text-xs" style={{ color: "#99A6AB" }}>
                            {profs.length}
                          </span>
                        </div>
                        {profs.map((p, i) => (
                          <motion.div
                            key={p.id}
                            layout
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2, delay: i * 0.03 }}
                          >
                            <ProfessionCard
                              profession={p}
                              onClick={openDetail}
                              recommended={recommendedProfessions.has(p.name)}
                            />
                          </motion.div>
                        ))}
                      </motion.div>
                    );
                  })
                ) : (
                  filtered.map((p, i) => (
                    <motion.div
                      key={p.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2, delay: i * 0.03 }}
                    >
                      <ProfessionCard
                        profession={p}
                        onClick={openDetail}
                        recommended={recommendedProfessions.has(p.name)}
                      />
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {view === "detail" && selectedProfession && (
          <motion.div key="detail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ProfessionDetail
              profession={selectedProfession}
              onBack={() => setView("list")}
              onSimulate={openSimulator}
            />
          </motion.div>
        )}

        {view === "simulator" && (
          <motion.div key="simulator" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <IncomeSimulator
              initialProfession={selectedProfession}
              onBack={() => setView(selectedProfession ? "detail" : "list")}
            />
          </motion.div>
        )}

        {view === "comparison" && (
          <motion.div key="comparison" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <CareerComparison onBack={() => setView("list")} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
